import { languagesData, USER_TYPES } from "@root/constants";
import { AuthService, ChatService, OrganizationService } from "@root/services";
import { LocalStorageService } from "@root/services/local-storage";
import { useState, useEffect } from "react";
import { useNotification } from "./useNotification";
import { getLoggedInUser } from "@root/store/selectors/entities";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { HistoryService } from "@root/services/history/Index";

// To be used for global state management. Not specific to any module.
// To avoid using redux toolkit

export const useGlobal = () => {
  const languagesOptions = languagesData();
  const [organizationOptions, setOrganizationOptions] = useState([]);
  const [updateProfileLoading, setUpdateProfileLoading] = useState(false);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [verifyOtpLoading, setVerifyOtpLoading] = useState(false);
  const [isEmailVerifyOpen, setIsEmailVerifyOpen] = useState(false);
  const [userSubscriber, setUserSubscriber] = useState(null);
  const [historyCount, setHistoryCount] = useState(0);
  const [userData, setUserData] = useState<any>(null);
  const [historyPage, setHistoryPage] = useState(1);
  const { t } = useTranslation("common");

  const orgService = new OrganizationService();
  const authService = new AuthService();
  const chatService = new ChatService();
  const historyService = new HistoryService();
  const localStorageService = new LocalStorageService();
  const { showNotification } = useNotification();
  const user: any = useSelector(getLoggedInUser);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    // Read from sessionStorage (per-tab state)
    const saved = sessionStorage.getItem("sidebarOpen");
    if (saved !== null) {
      try {
        return JSON.parse(saved);
      } catch {
        return window.innerWidth >= 768;
      }
    }
    return window.innerWidth >= 768;
  });

  const [apiRetryError, setApiRetryError] = useState(false);
  const [isStatusZero, setIsStatusZero] = useState(false);
  const [userHistory, setUserHistory] = useState<any>(null);

  // Persist sidebar state to sessionStorage (per-tab) whenever it changes
  useEffect(() => {
    sessionStorage.setItem("sidebarOpen", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  const fetchOrganizations = async (payload?: any) => {
    try {
      const response = await orgService.fetchPublicCompanyNames(payload);
      const orgs = response?.data?.data || [];
      setOrganizationOptions(orgs);
    } catch (e) {
      setOrganizationOptions([]);
    }
  };

  const setCookies = async () => {
    try {
      await authService.setCookies();
    } catch (e) {
      console.warn(e);
    }
  };

  const updateProfile = async (id: number, payload: any) => {
    if (payload?.password) {
      setResetPasswordLoading(true);
    } else {
      setUpdateProfileLoading(true);
    }
    try {
      await authService.updateProfile(id, payload, user?.role);
      // Only trigger email verification if the email is changed and password is NOT being changed
      if (
        payload?.email &&
        user?.email !== payload?.email &&
        !payload?.password &&
        user?.role === USER_TYPES.JOURNALIST
      ) {
        setIsEmailVerifyOpen(true);
        return false;
      }
      if (
        payload?.email &&
        user?.email !== payload?.email &&
        !payload?.password &&
        user?.role === USER_TYPES.JOURNALIST
      ) {
        return true;
      } else {
        showNotification({
          type: "success",
          message: t("notifications.profileUpdateSuccess"),
        });
        getUserData();
        return true;
      }
    } catch (e) {
      showNotification({
        type: "error",
        message: `${t("notifications.profileUpdateError")}
          ${
            payload?.password
              ? t("notifications.password")
              : t("notifications.profile")
          }`,
      });
    } finally {
      if (payload?.password) {
        setResetPasswordLoading(false);
      } else {
        setUpdateProfileLoading(false);
      }
    }
  };

  const verifyOtp = async (payload: any): Promise<boolean> => {
    setVerifyOtpLoading(true);

    try {
      await authService.verifyOtp(payload); // success response
      return true;
    } catch (error) {
      showNotification({
        type: "error",
        message: t("notifications.incorrectOTP"),
      });
      return false;
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const resendOtp = async (data: any): Promise<boolean> => {
    setVerifyOtpLoading(true);
    const { payload, onSuccess, onError } = data;

    try {
      const response = await authService.sendOtp(payload); // add baseUrl if needed
      if (response?.error) throw response;

      onSuccess?.();
      showNotification({
        type: "success",
        message: t("common:emailVerification.resendSuccess"),
      });
      return true;
    } catch (error) {
      onError?.();
      showNotification({
        type: "error",
        message: t("Otp not sent"),
      });
      return false;
    } finally {
      setVerifyOtpLoading(false);
    }
  };

  const getUserData = async () => {
    try {
      const response = await chatService.getUserProfile();
      const updatedUserData = response?.data?.data;
      setUserData(updatedUserData);

      const { setAuthData } = await import("@root/store/slices/entities/auth");
      const { store } = await import("@root/store");
      store.dispatch(setAuthData(updatedUserData));
    } catch (error: any) {
      console.error(error);
      throw error;
    }
  };

  const getJournlaistSubscriber = async () => {
    // Skip API call if user is super admin (they don't have access to this endpoint)
    if (user?.role === USER_TYPES.SUPER_ADMIN) {
      setUserSubscriber(null);
      return;
    }
    try {
      const response = await chatService.getJournalistSubscriber();
      setUserSubscriber(response?.data?.data);
    } catch (error) {
      console.error(error);
      // Don't throw error for super admin, just set to null
      if (user?.role !== USER_TYPES.SUPER_ADMIN) {
        throw error;
      }
      setUserSubscriber(null);
    }
  };

  const getChatHistory = async (payload: any) => {
    try {
      const response = await historyService.fetchHistory(payload);
      console.log("getChatHistory response:", response);

      // Ensure we set a proper empty array if no data
      const historyData = response?.data?.data;
      if (Array.isArray(historyData)) {
        setHistoryCount(historyData[1]);
        setUserHistory(historyData);
      } else {
        console.log("Invalid history data format, setting empty array");
        setUserHistory([]);
      }
    } catch (error) {
      console.error("Error fetching chat history:", error);
      setUserHistory([]); // Set empty array on error
      throw error;
    }
  };

  // Persist sidebar state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem("sidebarOpen", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  return {
    isSidebarOpen,
    setIsSidebarOpen,
    languagesOptions,
    apiRetryError,
    setApiRetryError,
    isStatusZero,
    setIsStatusZero,
    fetchOrganizations,
    organizationOptions,
    updateProfile,
    updateProfileLoading,
    isEmailVerifyOpen,
    setIsEmailVerifyOpen,
    resendOtp,
    getUserData,
    userData,
    verifyOtpLoading,
    verifyOtp,
    resetPasswordLoading,
    getChatHistory,
    userHistory,
    setCookies,
    getJournlaistSubscriber,
    userSubscriber,
    setUserSubscriber,
    historyCount,
    historyPage,
    setHistoryPage,
  };
};
